import React, {FC, ReactElement} from "react";
import {Button} from "@wakelet/ui-kit";
import {IconApple, IconClasslink, IconClever, IconFacebook, IconGoogle, IconMicrosoft} from "../icons/sso";
import {useNavigate} from "react-router";
import {AuthProviderType} from "../../domain/providers";
import {startLogin} from "../../domain/login";
import {signInWithSsoProvider} from "../../firebase";
import {translateKey} from "../language/translate";
import {IconEmail} from "../icons/icons";

type Props = {
  text: string;
  icon?: React.ReactNode
  signIn?: () => void;
  "data-testid": string;
  "data-id": string;
}


const SSOButton: FC<Props> = ({text, icon, signIn, "data-testid": dataTestId, "data-id": dataId}) => {
  return <div className="w-[300px] flex flex-col items-center mb-2" data-testid={dataTestId} data-id={dataId}>
    <Button className="w-80 border" variant="secondary" onClick={signIn}>
      <Button.Label text={text} icon={icon}/>
    </Button>
  </div>
};



export const useSsoSignIn = () => {
  const signInWith = (provider: AuthProviderType): Promise<never> => {
    startLogin(provider);
    return signInWithSsoProvider(provider)
  }
  return {signInWith}
};

export const AppleSSO = () => {
  const {signInWith} = useSsoSignIn();
  return <SSOButton
      data-id="apple-sso"
      data-testid="apple-sso-btn"
      text={translateKey("account:sso.apple")}
      icon={<IconApple/>}
      signIn={() => signInWith(AuthProviderType.Apple)}
  />;
};

export const GoogleSSO = () => {
  const {signInWith} = useSsoSignIn();
  return <SSOButton
      data-testid="google-sso-btn"
      data-id="google-sso"
      text={translateKey("account:sso.google")}
      icon={<IconGoogle/>}
      signIn={() => signInWith(AuthProviderType.Google)}
  />;
}

export const FacebookSSO = () => {
  const {signInWith} = useSsoSignIn();
  return <SSOButton
      data-testid="facebook-sso-btn"
      data-id="facebook-sso"
      text={translateKey("account:sso.facebook")}
      icon={<IconFacebook/>}
      signIn={() => signInWith(AuthProviderType.Facebook)}
  />;
}

export const CleverSSO = () => {
  const navigate = useNavigate();
  return <SSOButton
      data-testid="clever-sso-btn"
      data-id="clever-sso"
      text={translateKey("account:sso.clever")}
      icon={<IconClever/>}
      signIn={() => {
        navigate("/login/clever");
      }}
  />;
}

export const ClasslinkSSO = () => {
  const navigate = useNavigate();
  return <SSOButton
      data-id="classlink-sso"
      data-testid={"classlink-sso-btn"}
      text={translateKey("account:sso.classlink")}
      icon={<IconClasslink size={40} />}
      signIn={() => {
        navigate("/login/classlink");
      }}
  />;
}

export const MicrosoftSSO = () => {
  const {signInWith} = useSsoSignIn();
  return <SSOButton
      data-testid="microsoft-sso-btn"
      data-id="microsoft-sso"
      text={translateKey("account:sso.microsoft")}
      icon={<IconMicrosoft/>}
      signIn={() => signInWith(AuthProviderType.Microsoft)}
  />;
}

export const EmailLoginButton = (to?: string) => {
  const navigate = useNavigate();
  return <SSOButton
      data-id="login-with-email"
      data-testid={"login-with-email-btn"}
      text={"Continue with email"}
      icon={<IconEmail/>}
      signIn={() => {
        navigate(to || "/login");
      }}
  />;
}


const providerButtons: { [key: string]: ReactElement } = {
  "apple.com": <AppleSSO/>,
  "google.com": <GoogleSSO/>,
  "facebook.com": <FacebookSSO/>,
  "gc.apple.com": <CleverSSO/>,
  "playgames.google.com": <ClasslinkSSO/>,
  "microsoft.com": <MicrosoftSSO/>
}
export const toProviderButton = (provider: string): ReactElement | undefined => {
  return providerButtons[provider];
}
